"use client"
import { useEffect, useRef, useState } from "react"

/**
 * Dropdown for a room count, with no ceiling.
 *
 * The common answers are one tap away: a dropdown from `min` to 10. Choosing
 * "More than 10" reveals a number box for anything larger, so a lodge, a
 * boarding house or a block of flats can be described accurately.
 *
 * This replaced dropdowns that silently capped real properties: bedrooms
 * stopped at 6, bathrooms at 5, and room-share "rooms available" at a "5+"
 * button that actually saved 5. The API applies a generous sanity ceiling
 * (1000) so a slipped key cannot store 60000.
 *
 * The value is always a whole-number string.
 */
const DROPDOWN_MAX = 10
const MORE = "__more__"

export default function CountInput({
  value,
  onChange,
  min = 0,
  label,
  zeroLabel,
  id,
}: {
  value: string | number
  onChange: (next: string) => void
  min?: number
  label: string
  /** What to show for 0, e.g. "N/A" for bedrooms on land. */
  zeroLabel?: string
  id?: string
}) {
  const parsed = Number.parseInt(String(value), 10)
  const current = Number.isFinite(parsed) ? parsed : min

  // Stay in custom mode while the box is being edited, even if the number
  // briefly drops to 10 or below mid-typing.
  const [custom, setCustom] = useState(current > DROPDOWN_MAX)
  const boxRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (current > DROPDOWN_MAX) setCustom(true)
  }, [current])

  const field: React.CSSProperties = {
    width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8,
    fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none",
  }

  const options: number[] = []
  for (let i = min; i <= DROPDOWN_MAX; i++) options.push(i)

  return (
    <div>
      <select
        id={id}
        aria-label={label}
        value={custom ? MORE : String(Math.min(current, DROPDOWN_MAX))}
        onChange={e => {
          if (e.target.value === MORE) {
            setCustom(true)
            if (current <= DROPDOWN_MAX) onChange(String(DROPDOWN_MAX + 1))
            // Next paint, so the box exists before we focus it.
            setTimeout(() => boxRef.current?.select(), 0)
          } else {
            setCustom(false)
            onChange(e.target.value)
          }
        }}
        style={field}
      >
        {options.map(n => (
          <option key={n} value={String(n)}>{n === 0 && zeroLabel ? zeroLabel : n}</option>
        ))}
        <option value={MORE}>More than {DROPDOWN_MAX}</option>
      </select>

      {custom && (
        <input
          ref={boxRef}
          aria-label={`${label}, exact number`}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder={`e.g. ${DROPDOWN_MAX + 2}`}
          value={String(value)}
          onChange={e => onChange(e.target.value.replace(/\D/g, ""))}
          onBlur={() => {
            // Resolve an empty or too-small box on the way out, and drop back to
            // the dropdown if the number fits in it.
            const n = Number.parseInt(String(value), 10)
            const safe = Number.isFinite(n) ? Math.max(min, n) : min
            onChange(String(safe))
            if (safe <= DROPDOWN_MAX) setCustom(false)
          }}
          style={{ ...field, marginTop: 8, fontWeight: 600, color: "var(--navy)" }}
        />
      )}
    </div>
  )
}
